import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@/hooks/useAuth';
import { likeService } from '@/features/likes/services/likeService';
import { authService } from '@/features/user/services/authService';
import type { LikeableEntityType } from '@/features/likes/types/likeTypes';

interface LikeButtonProps {
  entityId: string;
  entityType: LikeableEntityType;
  isLiked: boolean;
  onToggle: (newLikedState: boolean) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Heart-shaped like button with Instagram/YouTube-style animations.
 * Renders for all users. Unauthenticated users are registered as guests on first like.
 */
const LikeButton: React.FC<LikeButtonProps> = ({
  entityId,
  entityType,
  isLiked,
  onToggle,
  className = '',
  size = 'md',
}) => {
  const { isAuthenticated, refetch: refetchAuth } = useAuth();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24,
  };

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    e.preventDefault();

    // If already registering, ignore clicks
    if (isRegistering) return;

    // Immediately update UI (optimistic update)
    const newLikedState = !isLiked;
    onToggle(newLikedState);
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    // If unauthenticated, register as guest first
    if (!isAuthenticated) {
      try {
        setIsRegistering(true);
        await authService.registerGuest();
        await refetchAuth(); // Update auth state
      } catch (error) {
        console.error('Failed to register guest:', error);
        // Revert optimistic update on registration failure
        onToggle(isLiked);
        setIsRegistering(false);
        return;
      } finally {
        setIsRegistering(false);
      }
    }

    // Fire-and-forget API call - don't block UI
    likeService.toggleLike({
      entity_id: entityId,
      entity_type: entityType,
    }).catch((error) => {
      // Silently handle errors - state will sync on next page load
      console.error('Failed to toggle like:', error);
    });
  };

  const iconSize = iconSizes[size];

  return (
    <motion.button
      onClick={handleClick}
      className={`
        ${sizeClasses[size]}
        flex items-center justify-center
        rounded-full
        bg-black/40 backdrop-blur-sm
        hover:bg-black/60
        transition-colors duration-200
        cursor-pointer
        ${className}
      `}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      aria-label={isLiked ? 'Unlike' : 'Like'}
    >
      <AnimatePresence mode="wait">
        {isLiked ? (
          <motion.svg
            key="filled"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            initial={{ scale: 0 }}
            animate={{ 
              scale: isAnimating ? [1, 1.3, 1] : 1,
            }}
            exit={{ scale: 0 }}
            transition={{ 
              duration: 0.3,
              ease: "easeOut"
            }}
          >
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              fill="#ef4444"
            />
          </motion.svg>
        ) : (
          <motion.svg
            key="outline"
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            transition={{ duration: 0.2 }}
          >
            <path
              d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
              stroke="white"
              strokeWidth="2"
              fill="none"
            />
          </motion.svg>
        )}
      </AnimatePresence>

      {/* Burst animation on like */}
      <AnimatePresence>
        {isAnimating && isLiked && (
          <>
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-red-400"
                initial={{ 
                  scale: 0, 
                  x: 0, 
                  y: 0,
                  opacity: 1 
                }}
                animate={{ 
                  scale: [0, 1, 0],
                  x: Math.cos((i * 60 * Math.PI) / 180) * 20,
                  y: Math.sin((i * 60 * Math.PI) / 180) * 20,
                  opacity: [1, 1, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.4,
                  ease: "easeOut"
                }}
              />
            ))}
          </>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

export default LikeButton;
